import React from "react";
import ReactDOM from "react-dom";
import { ErrorBoundary } from "react-error-boundary";
import useSWR, { SWRConfig, useSWRConfig } from "swr";
import { uid } from "uid/single";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { fetcherWithToken } from "./fetcher-with-token";
import { vars } from "./theme.css";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { isEmpty } from "lodash";
import { Header, Drawer } from "@vagastec/ensemble-core";
import { Footer, Text } from "@vagastec/ensemble-core";
import "@vagastec/ensemble-core/dist/index.css";
import { Step0 } from "./step-0";
import "./global.css";
import { Forms } from "./forms";
import { VagaCriada } from "./vaga-criada";
import { Erro } from "./tela-erro";
import { IconePhoneCall } from "./icons/icone-phone-call";
import { IconeEnvelope } from "./icons/icone-envelope";
import { IconeCalender } from "./icons/icone-calender";
import {
  containerAjudaStyle,
  agendarTreinamentoStyle,
  containerLinksAjudaStyle,
  linkAjudaStyle,
  pageStyle,
  mainStyle,
} from "./index.css";
import { Space } from "./components/space";
import { separatorStyle } from "./step-4.css";
import { IconeIdCard } from "./icons/icone-id-card";
import { VagaModeloCriada } from "./vaga-modelo-criada";
import { IntlProvider } from "react-intl";
import { LocaleContext } from "./locale-context";
import en_US from "./locales/en_US.json";
import es_ES from "./locales/es_ES.json";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { FakeLogin } from "./fake-login";
import { getCookie, setCookie } from "./cookies";
import { PaginaVaga } from "./pagina-vaga";
import gtm from "./utils/gtm";

if (process.env.NODE_ENV === "production") {
  datadogRum.init({
    applicationId: "8331f603-d2ed-437f-a947-5279a10b296e",
    clientToken: "pub71699fc90f263310d1359c7f32a47870",
    site: "datadoghq.com",
    service: "odisseia",
    env: "main",
    // Specify a version number to identify the deployed version of your application in Datadog
    version: "0.0.1",
    sampleRate: 100,
    premiumSampleRate: 0,
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    useCrossSiteSessionCookie: true,
  });

  datadogLogs.init({
    clientToken: "pub71699fc90f263310d1359c7f32a47870",
    site: "datadoghq.com",
    forwardErrorsToLogs: true,
    sampleRate: 100,
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    useCrossSiteSessionCookie: true,
  });
}

const isPaginaVaga = location.pathname?.match(/\/v\d+$/);

const currencyIdToIso = {
  1: "BRL",
  2: "USD",
};

export const initialFormState = {
  vagaId: "",
  cargo: "",
  posicoesQuantity: 1,
  isPcd: false,
  salarioFaixaValorInicial: 0,
  salarioFaixaValorFinal: 0,
  salarioMoeda: "BRL",
  exibirSalario: false,
  descricao: "",
  beneficiosQuery: "",
  beneficiosSelected: [],
  modeloContratacaoSelectedId: null,
  nivelHierarquicoSelectedId: null,
  enderecoQuery: "",
  enderecoApiPais: "",
  enderecoApiEstado: "",
  enderecoApiCidade: "",
  enderecoApiBairro: "",
  enderecoApiRua: "",
  enderecoApiNumero: "",
  enderecoLat: null,
  enderecoLng: null,
  enderecoFormatado: "",
  enderecoRestricao: "",
  localTrabalhoSelectedId: null,
  escolaridadeMinimaSelectedId: null,
  areasAtuacaoSelected: [],
  idiomasQuery: "",
  idiomasSelected: [],
  outrosRequisitos: "",
  fasesQuery: "",
  fasesSelected: [],
  isAnuncioConfidencial: null,
  tipoApresentacao: null,
  apresentacaoId: null,
  apresentacaoNomeModelo: "",
  apresentacaoNomeEmpresa: "",
  apresentacaoLogo: "",
  apresentacaoDescricao: "",
  isSuspenderVaga: false,
  fichasSelected: [
    { uid: uid(), fichaSelecionadaId: "", isPreenchimentoObrigatorio: false },
  ],
  isEnviarEmail: true,
  isExibirRemetente: false,
  isParceiroEtalentSelecionado: false,
  isIndicacaoSelecionada: false,
  tipoIndicacao: "",
  emailsIndicacaoInput: "",
  emailsIndicacao: [],
  divisaoSelectedId: null,
  divisoesAcessoQuery: "",
  divisoesAcessoSelected: [],
  subdivisoesQuery: "",
  subdivisoesSelected: [],
  isVagaTeste: false,
  canaisDivulgacaoQuery: "",
  canaisDivulgacaoSelected: [],
  grupos: "",
  senhaVagaTeste: "",
  isOrigemVagaModelo: false,
  tempOrigemVaga: {},
  dataInicio: new Date().toISOString().slice(0, 10),
  dataFim: new Date(new Date().setMonth(new Date().getMonth() + 1))
    .toISOString()
    .slice(0, 10),
  isVagaPending: false,
  isPresetPending: false,
  isCargoLocked: false,
  isPcdLocked: false,
  isNivelHierarquicoLocked: false,
  isModeloContratacaoLocked: false,
  isPosicoesLocked: false,
  isLocalLocked: false,
  isSalarioLocked: false,
  isDescricaoLocked: false,
  isBeneficiosLocked: false,
  isEscolaridadeLocked: false,
  isAreasAtuacaoLocked: false,
  isIdiomasLocked: false,
  isOutrosRequisitosLocked: false,
  isFasesLocked: false,
  isApresentacaoEmpresaLocked: false,
  isPeriodoInscricaoLocked: false,
  isFichasLocked: false,
  isEnviarEmailLocked: false,
  isExibirRemetenteLocked: false,
  isParceirosLocked: false,
  isIndicacaoLocked: false,
  isDivisaoAcessoLocked: false,
  isCanaisDivulgacaoLocked: false,
};

const formReducer = (prevState, currState) => {
  return {
    ...prevState,
    ...(typeof currState === "function" ? currState(prevState) : currState),
  };
};

function AberturaProcessoSeletivo() {
  const isEditingVaga =
    location.pathname?.includes("/editar-processo") ||
    location.pathname?.includes("/editar-vaga-modelo");

  const [formState, setFormState] = React.useReducer(
    formReducer,
    initialFormState
  );

  const query = new URLSearchParams(location.search);

  const intl = useIntl();

  const idiomasNiveis = [
    {
      id: 2,
      label: intl.formatMessage(
        defineMessage({
          id: "step1.idiomaBasico",
          defaultMessage: "Básico",
        })
      ),
    },
    {
      id: 3,
      label: intl.formatMessage(
        defineMessage({
          id: "step1.idiomaIntermediario",
          defaultMessage: "Intermediário",
        })
      ),
    },
    {
      id: 4,
      label: intl.formatMessage(
        defineMessage({
          id: "step1.idiomaAvancado",
          defaultMessage: "Avançado",
        })
      ),
    },
    {
      id: 5,
      label: intl.formatMessage(
        defineMessage({
          id: "step1.idiomaFluente",
          defaultMessage: "Fluente",
        })
      ),
    },
  ];

  function convertText(text) {
    return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
  }

  const { data: painelData, error: painelError } = useSWR(
    `${process.env.BASE_URL}/painel`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (painelData) {
      gtm();
      setFormState({
        painelData,
        nomeRemetente: painelData.funcionario?.nome ?? "",
        emailRemetente: painelData.funcionario?.email ?? "",
        gerenteComercial: painelData.cliente.responsavel_conta?.nome ?? "",
        emailGerenteComercial:
          painelData.cliente?.responsavel_conta?.email ?? "",
      });
    }
    if (painelError) setFormState({ painelError });
  }, [painelData, painelError]);

  // isValidating indica se há uma requisição ou uma revalidação carregando
  const {
    data: vagaData,
    error: vagaError,
    isValidating: vagaIsValidating,
  } = useSWR(
    () =>
      formState.vagaId
        ? `${process.env.HESTIA}/anuncios/${formState.vagaId}`
        : null,
    fetcherWithToken
  );

  const {
    data: presetVaga,
    error: presetVagaError,
    isValidating: presetIsValidating,
  } = useSWR(
    () =>
      query.get("requisicao")
        ? `${process.env.HESTIA}/anuncio_presetado/${query.get("requisicao")}`
        : null,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (presetVaga) setFormState({ presetVaga });
    if (presetVagaError) setFormState({ presetVagaError });
  }, [presetVaga, presetVagaError]);

  React.useEffect(() => {
    setFormState({
      isVagaPending: !vagaData && !vagaError && vagaIsValidating,
      isPresetPending: !presetVaga && !presetVagaError && presetIsValidating,
    });

    if (!isEmpty(presetVaga) && !vagaData)
      setFormState({
        presetVaga,
        cargo: presetVaga.titulo ?? "",
        descricao: convertText(presetVaga.descricao) ?? "",
        posicoesQuantity: presetVaga.posicoes ?? 1,
      });

    if (vagaError) setFormState({ vagaError });

    if (vagaData) {
      const controleEdicaoCampos = vagaData?.anuncios_controle_edicao_campos[0];

      setFormState({
        vagaData,
        cargo: (presetVaga?.titulo ? presetVaga.titulo : vagaData.titulo) ?? "",
        posicoesQuantity:
          (presetVaga?.posicoes ? presetVaga.posicoes : vagaData.posicoes) ?? 1,
        isPcd: vagaData.pcd ?? false,
        salarioFaixaValorInicial: vagaData.salario?.minimo ?? 0,
        salarioFaixaValorFinal: vagaData.salario?.maximo ?? 0,
        salarioMoeda: currencyIdToIso[vagaData.salario?.moeda_id] ?? "BRL",
        exibirSalario: !vagaData.salario?.combinar ?? false,
        descricao:
          (presetVaga?.descricao
            ? convertText(presetVaga.descricao)
            : vagaData.descricao) ?? "",
        beneficiosSelected: vagaData.beneficios
          ? vagaData.beneficios.map((v) => ({ ...v, value: v.valor }))
          : [],
        modeloContratacaoSelectedId: vagaData.modelo_contratacao?.id ?? null,
        nivelHierarquicoSelectedId: vagaData.nivel_hierarquico?.id ?? null,
        enderecoQuery: vagaData.localizacao?.localizacao_completa ?? "",
        enderecoFormatado: vagaData.localizacao?.localizacao_completa ?? "",
        enderecoApiPais: vagaData.localizacao?.pais ?? "",
        enderecoApiEstado: vagaData.localizacao?.estado ?? "",
        enderecoApiCidade: vagaData.localizacao?.cidade ?? "",
        enderecoApiBairro: vagaData.localizacao?.bairro ?? "",
        enderecoApiRua: vagaData.localizacao?.rua ?? "",
        enderecoApiNumero: vagaData.localizacao?.numero ?? null,
        enderecoLat: vagaData.localizacao?.latitude
          ? Number(vagaData.localizacao?.latitude)
          : "",
        enderecoLng: vagaData.localizacao?.longitude
          ? Number(vagaData.localizacao?.longitude)
          : "",
        enderecoRestricao: vagaData.localizacao?.outras_localidades,
        localTrabalhoSelectedId: vagaData.modelo_local_trabalho?.id ?? null,
        escolaridadeMinimaSelectedId: vagaData.nivel_escolaridade?.id ?? null,
        areasAtuacaoSelected:
          vagaData.areas_atuacao?.map((v) => ({ ...v, descricao: v.label })) ??
          [],
        idiomasSelected:
          vagaData.idiomas?.map((v) => ({
            ...v,
            levelId: v.nivel_id,
            uid: `${v.id}-${v.nivel_id}`,
            levelLabel: idiomasNiveis.find((level) => level.id === v.nivel_id)
              .label,
            label: `${
              idiomasData.find((idioma) => idioma.id === v.id).nome
            } - ${
              idiomasNiveis.find((level) => level.id === v.nivel_id).label
            }`,
          })) ?? [],
        outrosRequisitos: vagaData.requisitos ?? "",
        fasesSelected: vagaData.fases ?? [],
        isAnuncioConfidencial: vagaData.confidencial ?? null,
        apresentacaoId: vagaData.modelo_de_apresentacao?.id ?? null,
        tipoApresentacao: vagaData.modelo_de_apresentacao?.id
          ? "utilizando-modelo"
          : null,
        isSuspenderVaga: isEditingVaga ? vagaData.suspender_vaga : false,
        fichasSelected: vagaData.fichas?.length
          ? vagaData.fichas.map((v) => ({
              uid: uid(),
              fichaSelecionadaId: v.id,
              isPreenchimentoObrigatorio: v.ficha_auto,
            }))
          : [
              {
                uid: uid(),
                fichaSelecionadaId: "",
                isPreenchimentoObrigatorio: false,
              },
            ],
        isEnviarEmail: vagaData.enviar_mensagem ?? false,
        emailMensagem: vagaData.modelo_mensagem?.html ?? "",
        isExibirRemetente: !vagaData.ocultar_remetente ?? false,
        modeloEmailSelectedId: vagaData.modelo_mensagem?.id ?? null,
        isParceiroEtalentSelecionado: vagaData.parceiros?.some(
          (v) => v.id === 1
        ),
        isIndicacaoSelecionada:
          vagaData.indicacao_candidato === 1 ||
          vagaData.indicacao_candidato === 2,
        tipoIndicacao:
          vagaData.indicacao_candidato === 2
            ? "indicacao-livre"
            : vagaData.indicacao_candidato === 1
            ? "indicacao-restrita"
            : "",
        divisaoSelectedId: vagaData.anuncio_divisao_id ?? null,
        divisoesAcessoSelected: vagaData.divisoes_acesso ?? [],
        subdivisoesSelected:
          vagaData.subdivisoes?.map((v) => ({
            ...v,
            label: `${v.label_divisao} - ${v.label}`,
          })) ?? [],
        isVagaTeste: vagaData.teste ?? false,
        canaisDivulgacaoSelected: vagaData.canais_publicacao ?? [],
        senhaVagaTeste: vagaData.senha ?? "",
        isOrigemVagaModelo: vagaData.vaga_modelo ?? false,
        dataInicio:
          isEditingVaga && vagaData.data_abertura
            ? vagaData.data_abertura
            : new Date().toISOString().slice(0, 10),
        dataFim:
          isEditingVaga && vagaData.data_expiracao
            ? vagaData.data_expiracao
            : new Date(new Date().setMonth(new Date().getMonth() + 1))
                .toISOString()
                .slice(0, 10),
        isCargoLocked: !controleEdicaoCampos?.titulo,
        isPcdLocked: !controleEdicaoCampos?.pcd,
        isNivelHierarquicoLocked: !controleEdicaoCampos?.nivel_hierarquico,
        isModeloContratacaoLocked: !controleEdicaoCampos?.modelo_contratacao,
        isPosicoesLocked: !controleEdicaoCampos?.posicoes,
        isLocalLocked:
          !controleEdicaoCampos?.localizacao &&
          vagaData.localizacao &&
          vagaData.modelo_local_trabalho,
        isSalarioLocked: !controleEdicaoCampos?.salario,
        isDescricaoLocked:
          !controleEdicaoCampos?.descricao && vagaData.descricao?.length >= 300,
        isBeneficiosLocked: !controleEdicaoCampos?.beneficios,
        isEscolaridadeLocked: !controleEdicaoCampos?.escolaridade,
        isAreasAtuacaoLocked: !controleEdicaoCampos?.areas_atuacao,
        isIdiomasLocked: !controleEdicaoCampos?.idiomas,
        isOutrosRequisitosLocked: !controleEdicaoCampos?.requisitos,
        isFasesLocked: !controleEdicaoCampos?.fases,
        isApresentacaoEmpresaLocked: !controleEdicaoCampos?.template,
        isFichasLocked: !controleEdicaoCampos?.fichas,
        isEnviarEmailLocked: !controleEdicaoCampos?.modelo_mensagem,
        isExibirRemetenteLocked: !controleEdicaoCampos?.email_remetente,
        isParceirosLocked: !controleEdicaoCampos?.parceiros,
        isIndicacaoLocked: !controleEdicaoCampos?.aceitar_indicacao,
        isCanaisDivulgacaoLocked: !controleEdicaoCampos?.canais_divulgacao,
      });
    }
  }, [
    vagaData,
    vagaError,
    presetVaga,
    vagaIsValidating,
    presetVagaError,
    presetIsValidating,
  ]);

  const { data: niveisHierarquicosData, error: niveisHierarquicosError } =
    useSWR(`${process.env.HESTIA}/niveis_hierarquicos`, fetcherWithToken);

  React.useEffect(() => {
    if (niveisHierarquicosData) setFormState({ niveisHierarquicosData });
    if (niveisHierarquicosError) setFormState({ niveisHierarquicosError });
  }, [niveisHierarquicosData, niveisHierarquicosError]);

  const { data: beneficiosData, error: beneficiosError } = useSWR(
    `${process.env.HESTIA}/beneficios?ativo=true`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (beneficiosData) setFormState({ beneficiosData });
    if (beneficiosError) setFormState({ beneficiosError });
  }, [beneficiosData, beneficiosError]);

  const { data: modelosContratacaoData, error: modelosContratacaoError } =
    useSWR(`${process.env.HESTIA}/modelos_contratuais`, fetcherWithToken);

  React.useEffect(() => {
    if (modelosContratacaoData) setFormState({ modelosContratacaoData });
    if (modelosContratacaoError) setFormState({ modelosContratacaoError });
  }, [modelosContratacaoData, modelosContratacaoError]);

  const { data: escolaridadeMinimaData, error: escolaridadeMinimaError } =
    useSWR(`${process.env.HESTIA}/niveis_escolaridade`, fetcherWithToken);

  React.useEffect(() => {
    if (escolaridadeMinimaData) setFormState({ escolaridadeMinimaData });
    if (escolaridadeMinimaError) setFormState({ escolaridadeMinimaError });
  }, [escolaridadeMinimaData, escolaridadeMinimaError]);

  const { data: locaisTrabalhoData, error: locaisTrabalhoError } = useSWR(
    `${process.env.HESTIA}/modelos_locais_trabalho`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (locaisTrabalhoData) setFormState({ locaisTrabalhoData });
    if (locaisTrabalhoError) setFormState({ locaisTrabalhoError });
  }, [locaisTrabalhoData, locaisTrabalhoError]);

  const { data: areasAtuacaoData, error: areasAtuacaoError } = useSWR(
    `${process.env.DOMINIOS}/setores`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (areasAtuacaoData) setFormState({ areasAtuacaoData });
    if (areasAtuacaoError) setFormState({ areasAtuacaoError });
  }, [areasAtuacaoData, areasAtuacaoError]);

  const { data: idiomasData, error: idiomasError } = useSWR(
    `${process.env.DOMINIOS}/idiomas`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (idiomasData) setFormState({ idiomasData });
    if (idiomasError) setFormState({ idiomasError });
  }, [idiomasData, idiomasError]);

  const { data: fasesData, error: fasesError } = useSWR(
    `${process.env.HESTIA}/fases`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (fasesData) setFormState({ fasesData });
    if (fasesError) setFormState({ fasesError });
  }, [fasesData, fasesError]);

  const { data: apresentacoesEmpresaData, error: apresentacoesEmpresaError } =
    useSWR(`${process.env.HESTIA}/apresentacao_cliente`, fetcherWithToken);

  React.useEffect(() => {
    if (apresentacoesEmpresaData) setFormState({ apresentacoesEmpresaData });
    if (apresentacoesEmpresaError) setFormState({ apresentacoesEmpresaError });
  }, [apresentacoesEmpresaData, apresentacoesEmpresaError]);

  const { data: fichasData, error: fichasError } = useSWR(
    `${process.env.HESTIA}/fichas`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (fichasData) setFormState({ fichasData });
    if (fichasError) setFormState({ fichasError });
  }, [fichasData, fichasError]);

  const { data: parceirosData, error: parceirosError } = useSWR(
    `${process.env.HESTIA}/parceiros`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (parceirosData) setFormState({ parceirosData });
    if (parceirosError) setFormState({ parceirosError });
  }, [parceirosData, parceirosError]);

  const { data: creditosEtalentData, error: creditosEtalentError } = useSWR(
    `${process.env.HESTIA}/creditos`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (creditosEtalentData) setFormState({ creditosEtalentData });
    if (creditosEtalentError) setFormState({ creditosEtalentError });
  }, [creditosEtalentData, creditosEtalentError]);

  const { data: emailsIndicacaoData, error: emailsIndicacaoError } = useSWR(
    `${process.env.HESTIA}/emails_indicacao`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (emailsIndicacaoData)
      setFormState({
        emailsIndicacaoData,
        emailsIndicacao: emailsIndicacaoData,
      });
    if (emailsIndicacaoError) setFormState({ emailsIndicacaoError });
  }, [emailsIndicacaoData, emailsIndicacaoError]);

  const { data: modelosEmailData, error: modelosEmailError } = useSWR(
    `${process.env.HESTIA}/modelos_comunicacao`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (modelosEmailData) setFormState({ modelosEmailData });
    if (modelosEmailError) setFormState({ modelosEmailError });
  }, [modelosEmailData, modelosEmailError]);

  React.useEffect(() => {
    const mensagemPadrao = {
      id: 999999,
      label: intl.formatMessage(
        defineMessage({
          id: "select.mensagemPadraoLabel",
          defaultMessage: "Mensagem padrão",
        })
      ),
      html: intl.formatMessage(
        defineMessage({
          id: "select.mensagemPadraoHtml",
          defaultMessage:
            "Olá, tudo bem?<br /><br />Agradecemos seu interesse em fazer parte do nosso time. Estamos constantemente em busca de talentos como você, por isso te desejamos boa sorte.<br /><br />Para acompanhar sua evolução neste processo seletivo, acesse o VAGAS.com.br e consulte o histórico de candidaturas, na página de serviços ao candidato.<br /><br />Muito obrigado!",
        })
      ),
    };

    if (modelosEmailData) {
      const newModelosEmailData = [mensagemPadrao, ...modelosEmailData];
      setFormState({
        modelosEmailData: newModelosEmailData,
        modeloEmailSelectedId: newModelosEmailData[0].id,
        emailMensagem: newModelosEmailData[0].html,
      });
    } else {
      setFormState({
        modeloEmailSelectedId: mensagemPadrao.id,
        emailMensagem: mensagemPadrao.html,
      });
    }

    if (modelosEmailError) setFormState({ modelosEmailError });
  }, [modelosEmailData, modelosEmailError]);

  const { data: divisaoData, error: divisaoError } = useSWR(
    `${process.env.HESTIA}/divisoes?excluir_recrutamento_interno=${
      formState.isAnuncioConfidencial ?? false
    }`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (divisaoData)
      setFormState({
        divisaoData,
        divisaoSelectedId:
          divisaoData.length === 1
            ? divisaoData[0].id
            : vagaData?.anuncio_divisao_id ?? "",
      });
    if (divisaoError) setFormState({ divisaoError });
  }, [divisaoData, divisaoError, vagaData]);

  const { data: divisoesAcessoData, error: divisoesAcessoError } = useSWR(
    `${process.env.HESTIA}/divisoes?excluir_recrutamento_interno=false`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (divisoesAcessoData) setFormState({ divisoesAcessoData });
    if (divisoesAcessoError) setFormState({ divisoesAcessoError });
  }, [divisoesAcessoData, divisoesAcessoError]);

  const { data: responsavelData, error: responsavelError } = useSWR(
    `${process.env.HESTIA}/funcionarios`,
    fetcherWithToken
  );

  //  - Se o usuário estiver criando a vaga e a empresa tiver um responsável e/ou uma divisão, os valores precisam ser setados
  //  - Se o usuário estiver criando a vaga e a empresa possuir mais de um responsável, o select tem que ser exibido, mas deve vir sugerido o id do usuário logado
  //  - Se o usuário estiver editando a vaga, o responsável e precisa ser mantido de acordo com os dados da vaga de origem

  React.useEffect(() => {
    if (responsavelData && painelData) {
      if (isEditingVaga && vagaData) {
        setFormState({ responsavelSelectedId: vagaData.responsavel?.id });
      } else {
        if (responsavelData.length === 1) {
          setFormState({ responsavelSelectedId: responsavelData[0].id });
        } else {
          setFormState({ responsavelSelectedId: painelData.funcionario?.id });
        }
      }
    }
  }, [responsavelData, vagaData, painelData]);

  React.useEffect(() => {
    if (responsavelData)
      setFormState({
        responsavelData,
      });
    if (responsavelError) setFormState({ responsavelError });
  }, [responsavelData, responsavelError]);

  const { data: subdivisoesData, error: subdivisoesError } = useSWR(
    `${process.env.HESTIA}/subdivisoes`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (subdivisoesData) setFormState({ subdivisoesData });
    if (subdivisoesError) setFormState({ subdivisoesError });
  }, [subdivisoesData, subdivisoesError]);

  const { data: canaisDivulgacaoData, error: canaisDivulgacaoError } = useSWR(
    `${process.env.HESTIA}/canais_publicacao`,
    fetcherWithToken
  );

  React.useEffect(() => {
    if (canaisDivulgacaoData) setFormState({ canaisDivulgacaoData });
    if (canaisDivulgacaoError) setFormState({ canaisDivulgacaoError });
  }, [canaisDivulgacaoData, canaisDivulgacaoError]);

  const [locale, setLocale] = React.useContext(LocaleContext);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const { mutate } = useSWRConfig();
  React.useEffect(() => {
    mutate(`${process.env.HESTIA}/beneficios?ativo=true`);
    mutate(`${process.env.DOMINIOS}/setores`);
    mutate(`${process.env.HESTIA}/modelos_contratuais`);
    mutate(`${process.env.HESTIA}/niveis_escolaridade`);
    mutate(`${process.env.HESTIA}/niveis_hierarquicos`);
  }, [locale]);

  return (
    <div className={pageStyle}>
      <>
        {!isPaginaVaga ? (
          <>
            <Header
              env={process.env.NODE_ENV}
              navItems={[
                {
                  id: 0,
                  label: (
                    <FormattedMessage
                      id="header.inicio"
                      defaultMessage="Início"
                      description="É um link que leva o usuário para a home"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}`,
                },
                {
                  id: 1,
                  label: (
                    <FormattedMessage
                      id="header.vagas"
                      defaultMessage="Vagas"
                      description="A palavra vaga é sempre referente a vaga de emprego"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/vagas`,
                },
                {
                  id: 2,
                  label: (
                    <FormattedMessage
                      id="header.profissionais"
                      defaultMessage="Profissionais"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/profissionais`,
                },
                {
                  id: 3,
                  label: (
                    <FormattedMessage
                      id="header.relatorios"
                      defaultMessage="Relatórios"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/relatorios`,
                },
                {
                  id: 4,
                  label: (
                    <FormattedMessage
                      id="header.configuracoes"
                      defaultMessage="Configurações"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/configuracoes`,
                },
              ]}
              secondaryNavItems={[
                {
                  id: 3,
                  icon: "store",
                  accessibilityLabel: (
                    <FormattedMessage
                      id="header.marketplace"
                      defaultMessage="Marketplace"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/marketplace`,
                  hasNotification: false,
                },
                {
                  id: 4,
                  icon: "help-circle",
                  accessibilityLabel: "Ajuda",
                  as: "button",
                  onClick: () => setIsDrawerOpen(true),
                },
              ]}
              userNavItems={[
                {
                  id: 1,
                  icon: "lock",
                  label: (
                    <FormattedMessage
                      id="header.alterarSenha"
                      defaultMessage="Alterar senha"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/move-to/alterar-senha-funcionario`,
                },
                {
                  id: 2,
                  icon: "arrow-from-left",
                  label: (
                    <FormattedMessage
                      id="header.sair"
                      defaultMessage="Sair"
                      description="Sair do sistema. Logout"
                    />
                  ),
                  as: "link",
                  href: `${process.env.GLOBAL_URL}/move-to/finalizar-sessao`,
                },
              ]}
            />

            <Drawer
              isOpen={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              title={
                <FormattedMessage
                  id="drawer.title"
                  defaultMessage="Como podemos ajudar?"
                />
              }
              mainAction={{
                id: 0,
                label: (
                  <FormattedMessage
                    id="drawer.centralAjuda"
                    defaultMessage="Visite a central de ajuda"
                  />
                ),
                as: "link",
                target: "_blank",
                rel: "noopener noreferer",
                href: "https://ajuda-forbusiness.vagas.com.br/",
              }}
            >
              <Text as="p">
                {" "}
                <FormattedMessage
                  id="drawer.infoAtendimento"
                  defaultMessage="Atendimento somente em horário comercial."
                />
              </Text>
              <Space />

              {formState.gerenteComercial && formState.emailGerenteComercial ? (
                <>
                  <Text as="p" weight={600}>
                    <FormattedMessage
                      id="drawer.gerente"
                      defaultMessage="Gerente de Relacionamento"
                    />
                  </Text>

                  <div className={containerAjudaStyle}>
                    <IconeIdCard
                      fill={vars.color.foreground}
                      width={16}
                      height={16}
                    />
                    <Text as="p">{formState.gerenteComercial}</Text>
                  </div>

                  <div className={containerAjudaStyle}>
                    <IconeEnvelope
                      fill={vars.color.foreground}
                      width={16}
                      height={16}
                    />

                    <Text as="p"> {formState.emailGerenteComercial}</Text>
                  </div>
                  <div className={separatorStyle} />
                </>
              ) : null}

              <Text as="p" weight={600}>
                <FormattedMessage
                  id="drawer.suporte"
                  defaultMessage="Suporte"
                />
              </Text>

              <div className={containerAjudaStyle}>
                <IconePhoneCall
                  fill={vars.color.foreground}
                  width={16}
                  height={16}
                />
                <Text as="p">
                  <FormattedMessage
                    id="drawer.telefoneSuporte"
                    defaultMessage="(11) 4084-1112"
                  />{" "}
                </Text>
              </div>

              <div className={containerAjudaStyle}>
                <IconeEnvelope
                  fill={vars.color.foreground}
                  width={16}
                  height={16}
                />
                <Text as="p">
                  <Text as="p">
                    <FormattedMessage
                      id="drawer.emailSuporte"
                      defaultMessage="suporte.empresas@vagas.com"
                      description="não precisa de tradução"
                    />
                  </Text>
                </Text>
              </div>

              <div className={separatorStyle} />

              <Text as="p" size="md" weight={600}>
                <FormattedMessage
                  id="drawer.treinamento"
                  defaultMessage="Treinamento"
                />
              </Text>

              <Text as="p">
                <FormattedMessage
                  id="drawer.agendeTreinamento"
                  defaultMessage="Agende um treinamento gratuitamente"
                />{" "}
              </Text>
              <Space height=".5rem" />

              <div className={agendarTreinamentoStyle}>
                <IconeCalender
                  fill={vars.color.foreground}
                  width={16}
                  height={16}
                />
                <a
                  href="https://materiais.vagas.com.br/treinamentos-online"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Text as="p" size="sm" weight={600}>
                    <FormattedMessage
                      id="drawer.linkAgendar"
                      defaultMessage="Agendar"
                    />
                  </Text>
                </a>
              </div>

              <div className={separatorStyle} />
              <div>
                <Text as="p" size="md" weight={600}>
                  <FormattedMessage
                    id="drawer.duvidasFrequentes"
                    defaultMessage="Dúvidas mais frequentes"
                  />
                </Text>

                <div className={containerLinksAjudaStyle}>
                  <Space height=".25rem" />
                  <a
                    href="https://ajuda-forbusiness.vagas.com.br/pt-BR/articles/5551843-publicar-vagas#h_ccb16ca6e2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkAjudaStyle}
                  >
                    Como criar um novo cargo
                  </a>

                  <Space height=".25rem" />
                  <a
                    href="https://ajuda-forbusiness.vagas.com.br/pt-BR/articles/5551843-publicar-vagas#h_7cab09cefb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkAjudaStyle}
                  >
                    Boas práticas na publicação de vagas
                  </a>

                  <Space height=".25rem" />
                  <a
                    href="https://ajuda-forbusiness.vagas.com.br/pt-BR/articles/5551843-publicar-vagas#h_e3ce6e8489"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkAjudaStyle}
                  >
                    Recomendação inteligente de profissionais
                  </a>
                </div>
              </div>
            </Drawer>
          </>
        ) : null}
      </>

      <main className={mainStyle}>
        <Router basename={process.env.PUBLIC_URL}>
          <Switch>
            <Route path="/fake-login">
              <FakeLogin></FakeLogin>
            </Route>
            <Route
              path={[
                "/novo-processo",
                "/nova-vaga-modelo",
                "/editar-processo/:id",
                "/editar-vaga-modelo/:id",
              ]}
            >
              <Forms formState={formState} setFormState={setFormState} />
            </Route>
            <Route path="/vaga-criada">
              <VagaCriada formState={formState} setFormState={setFormState} />
            </Route>
            <Route path="/vaga-modelo-criada">
              <VagaModeloCriada
                formState={formState}
                setFormState={setFormState}
              />
            </Route>
            <Route path="/erro">
              <Erro />
            </Route>
            {/* <Route path="/v:id">
              <PaginaVaga />
            </Route> */}
            <Route path="/">
              <Step0 formState={formState} setFormState={setFormState} />
            </Route>
          </Switch>
        </Router>
      </main>

      {!isPaginaVaga ? (
        <Footer
          env={process.env.NODE_ENV}
          language={locale}
          setLanguage={(lang) => {
            setLocale(lang);
            setCookie("IntegracaoLoginIdioma", localesToCookie[lang]);
          }}
          languages={[
            { code: "pt-BR", label: "Português" },
            { code: "en", label: "Inglês" },
            { code: "es", label: "Español" },
          ]}
          languageLabel="Idioma"
          termsLabel={
            <FormattedMessage
              id="footer.termosUso"
              defaultMessage="Termos de uso"
            />
          }
          termsHref={`${process.env.GLOBAL_URL}/termos-de-uso`}
        />
      ) : null}
    </div>
  );
}

function ErrorFallback({ resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>Algo deu errado!</p>
      <button onClick={resetErrorBoundary}>Tente novamente</button>
    </div>
  );
}

function RenderPaginaVaga() {
  return (
    <Router>
      <Route path="/v:id">
        <PaginaVaga />
      </Route>
    </Router>
  );
}

// Função do próprio react-intl que transforma o json para o formato esperado pelo Transifex
// https://github.com/formatjs/formatjs/blob/main/packages/cli/src/formatters/transifex.ts#L24
const compile = (msgs) => {
  const results = {};
  for (const [id, msg] of Object.entries(msgs)) {
    results[id] = msg.string;
  }
  return results;
};

const messages = {
  en: compile(en_US),
  es: compile(es_ES),
};

const cookieToLocales = { _ptBR: "pt-BR", _esALT: "es", _enUS: "en" };

const localesToCookie = { "pt-BR": "_ptBR", es: "_esALT", en: "_enUS" };

function App() {
  const [locale, setLocale] = React.useState(
    cookieToLocales[getCookie("IntegracaoLoginIdioma")] ?? "pt-BR"
  );

  return (
    <React.StrictMode>
      <SWRConfig
        value={{
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
          shouldRetryOnError: true,
        }}
      >
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            // REVER
            window.location.href = "/";
          }}
        >
          <IntlProvider
            messages={messages[locale]}
            locale={locale}
            defaultLocale="pt-BR"
          >
            <LocaleContext.Provider value={[locale, setLocale]}>
              {/* {isPaginaVaga ? (
                <RenderPaginaVaga />
              ) : (
                <AberturaProcessoSeletivo />
              )} */}
              <AberturaProcessoSeletivo />
            </LocaleContext.Provider>
          </IntlProvider>
        </ErrorBoundary>
      </SWRConfig>
    </React.StrictMode>
  );
}

const root = document.getElementById("root");

ReactDOM.render(<App />, root);
